import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { GoldMedalIcon } from '../components/MedalCelebration';
import { Award, ArrowLeft, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const medalLabels = {
  work: 'Work Medal',
  personal: 'Personal Medal',
  altruism: 'Altruism Medal'
};

export const SharedMedalPage = () => {
  const { medalId } = useParams();
  const navigate = useNavigate();
  const [medal, setMedal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMedal = async () => {
      try {
        const res = await fetch(`${API}/api/medals/${medalId}/public`);
        if (!res.ok) throw new Error('Medal not found');
        setMedal(await res.json());
      } catch { setError('This medal could not be found.'); }
      finally { setLoading(false); }
    };
    fetchMedal();
  }, [medalId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading medal...</p>
      </div>
    );
  }

  if (error || !medal) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <Award className="w-12 h-12 text-muted-foreground/30" />
        <p className="text-muted-foreground">{error || 'Medal not found'}</p>
        <Button variant="outline" onClick={() => navigate('/')}>Go to Brioritizer</Button>
      </div>
    );
  }

  const gifUrl = `${API}/api/medals/${medalId}/gif/public`;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-amber-50 to-background dark:from-amber-950/20 dark:to-background">
      <header className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center">
            <Award className="w-4 h-4 text-white" />
          </div>
          <span className="text-lg font-bold font-['Manrope']">Brioritizer</span>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/')} className="gap-1.5" data-testid="shared-medal-cta">
          <ExternalLink className="w-3.5 h-3.5" /> Try Brioritizer
        </Button>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-card rounded-2xl shadow-xl border border-border overflow-hidden"
        >
          <div className="h-2 bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-600" />
          <div className="p-8 text-center space-y-5">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 12 }}
              className="mx-auto"
              style={{ width: 120, height: 120 }}
            >
              <GoldMedalIcon type={medal.medal_type} size={120} />
            </motion.div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-1">
                Medal Earned
              </p>
              <h1 className="text-2xl font-bold font-['Manrope']" data-testid="shared-medal-title">
                {medalLabels[medal.medal_type] || 'Medal'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Milestone: {medal.milestone} achievements
              </p>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed" data-testid="shared-medal-message">
              {medal.message}
            </p>

            <div className="pt-2 border-t border-border space-y-1">
              <p className="text-sm font-medium" data-testid="shared-medal-user">
                Earned by {medal.user_name}
              </p>
              {medal.awarded_at && (
                <p className="text-xs text-muted-foreground">
                  {format(new Date(medal.awarded_at), 'MMMM d, yyyy')}
                </p>
              )}
            </div>

            <a
              href={gifUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="shared-medal-gif-link">
                <Award className="w-3.5 h-3.5" /> View Medal GIF
              </Button>
            </a>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default SharedMedalPage;
